import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  MenuItem,
  Box,
  Stack,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import { X, CreditCard, Receipt, ChevronDown } from 'lucide-react';

const MEMBER_TYPES = [
  { value: 'courier', label: 'Kuriiri' },
  { value: 'sender',  label: 'Lähettäjä' },
  { value: 'any',     label: 'Molemmat' },
];

const MEMBER_ROLES = [
  { value: 'regular', label: 'Jäsen' },
  { value: 'admin',   label: 'Ylläpitäjä' },
];

const emptyForm = {
  userId:     '',
  name:       '',
  email:      '',
  phone:      '',
  memberType: 'any',
  role:       'regular',
  payment: { companyName: '', yNumber: '' },
  billing: { companyName: '', yNumber: '' },
};

/* ── Design primitives ── */

function SectionLabel({ icon: Icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {Icon && <Icon size={13} opacity={0.45} />}
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionCard({ children }) {
  return (
    <Box
      sx={{
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}

function Row({ children }) {
  return <Stack direction="row" spacing={1.5}>{children}</Stack>;
}

/* ── Main export ── */
export const MemberAddModal = ({ open, onClose, onCreate, loading }) => {
  const [form, setForm] = useState(emptyForm);
  const [showMore, setShowMore] = useState(false);

  // Reset the form each time the modal opens
  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setShowMore(false);
    }
  }, [open]);

  const set       = (field)          => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setNested = (section, field) => (e) => setForm((p) => ({ ...p, [section]: { ...p[section], [field]: e.target.value } }));

  const cleanSection = (section) => {
    const companyName = section.companyName.trim();
    const yNumber     = section.yNumber.trim();
    if (!companyName && !yNumber) return null;
    return { companyName: companyName || null, yNumber: yNumber || null };
  };

  const userIdValid = form.userId.trim().length > 0;

  const handleCreate = () => {
    if (!userIdValid) return;
    const cleaned = {
      userId:     form.userId.trim(),
      name:       form.name.trim()  || null,
      email:      form.email.trim() || null,
      phone:      form.phone.trim() || null,
      memberType: form.memberType,
      role:       form.role,
      payment:    cleanSection(form.payment),
      billing:    cleanSection(form.billing),
    };
    onCreate(cleaned);
  };

  const showPayment = form.memberType === 'courier' || form.memberType === 'any';
  const showBilling = form.memberType === 'sender'  || form.memberType === 'any';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>Lisää jäsen</Typography>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>

          {/* ── Perustiedot ── */}
          <Box>
            <SectionLabel>Perustiedot</SectionLabel>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <TextField
                label="Käyttäjätunnus (UID)"
                required
                fullWidth
                size="small"
                value={form.userId}
                onChange={set('userId')}
                sx={{ '& input': { fontFamily: 'monospace' } }}
                helperText="Ainoa pakollinen kenttä"
              />
              <Row>
                <TextField label="Nimi"       fullWidth size="small"              value={form.name}  onChange={set('name')} />
                <TextField label="Sähköposti" fullWidth size="small" type="email" value={form.email} onChange={set('email')} />
              </Row>
              <TextField label="Jäsentyyppi" fullWidth select size="small" value={form.memberType} onChange={set('memberType')}>
                {MEMBER_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>

          {/* ── Lisätiedot (collapsible) ── */}
          <Box>
            <Box
              onClick={() => setShowMore((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
                borderRadius: 1,
                mx: -1,
                px: 1,
                py: 0.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <SectionLabel>Lisätiedot</SectionLabel>
              <ChevronDown
                size={16}
                opacity={0.5}
                style={{
                  transform: showMore ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>

            <Collapse in={showMore} timeout="auto">
              <Stack spacing={3} sx={{ mt: 1.5 }}>
                <Row>
                  <TextField label="Puhelinnumero" fullWidth size="small" value={form.phone} onChange={set('phone')} />
                  <TextField label="Rooli" fullWidth select size="small" value={form.role} onChange={set('role')}>
                    {MEMBER_ROLES.map((r) => (
                      <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                  </TextField>
                </Row>

                {/* ── Maksutiedot ── */}
                {showPayment && (
                  <Box>
                    <Box sx={{ mb: 1.5 }}>
                      <SectionLabel icon={CreditCard}>Maksutiedot (kuriiri)</SectionLabel>
                    </Box>
                    <SectionCard>
                      <Row>
                        <TextField
                          label="Yrityksen nimi" fullWidth size="small"
                          value={form.payment.companyName}
                          onChange={setNested('payment', 'companyName')}
                        />
                        <TextField
                          label="Y-tunnus" fullWidth size="small"
                          value={form.payment.yNumber}
                          onChange={setNested('payment', 'yNumber')}
                          placeholder="1234567-8"
                        />
                      </Row>
                    </SectionCard>
                  </Box>
                )}

                {/* ── Laskutustiedot ── */}
                {showBilling && (
                  <Box>
                    <Box sx={{ mb: 1.5 }}>
                      <SectionLabel icon={Receipt}>Laskutustiedot (lähettäjä)</SectionLabel>
                    </Box>
                    <SectionCard>
                      <Row>
                        <TextField
                          label="Yrityksen nimi" fullWidth size="small"
                          value={form.billing.companyName}
                          onChange={setNested('billing', 'companyName')}
                        />
                        <TextField
                          label="Y-tunnus" fullWidth size="small"
                          value={form.billing.yNumber}
                          onChange={setNested('billing', 'yNumber')}
                          placeholder="1234567-8"
                        />
                      </Row>
                    </SectionCard>
                  </Box>
                )}
              </Stack>
            </Collapse>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>Peruuta</Button>
        <Button
          variant="contained" onClick={handleCreate}
          disabled={loading || !userIdValid}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Lisätään...' : 'Lisää jäsen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};